import { push, replace } from "connected-react-router"
import { Menu } from "dash-styled"
import React, { FC, useCallback, useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import {
    createHumanWorker,
    removeWorker,
    createBotOrCrowdWorker
} from "../../../../../Actions/API/WorkerActions"
import { updateWorkerGroup } from "../../../../../Actions/API/WorkerGroupActions"
import { getEmail } from "../../../../../Helpers/Functions/Selectors/auth"
import { useModal } from "../../../../../Hooks/useModal"
import { useAppState } from "../../../../../Hooks/useReduxState"
import { useSelection } from "../../../../../Hooks/useSelection"
import { useWorkerList } from "../../../../../Hooks/useWorkerList"
import {
    getWorkerId,
    APIWorker,
    getWorkerDisplayText
} from "../../../../../Models/API/Worker"
import { APIWorkerGroup } from "../../../../../Models/API/WorkerGroup"
import { APIWorkerType } from "../../../../../Models/API/WorkerType"
import { getValue } from "../../../../../Models/Client/FetchState"
import { SortExpression } from "../../../../../Models/Client/SortExpression"
import { MainThunkDispatch } from "../../../../../Reducers"
import { routePaths } from "../../../../../Routes"
import { SortChooserProps } from "../../../../Common/Button/SortChooser"
import { Confirm } from "../../../../Common/Modal/Confirm"
import { Table } from "resha"
import { TableHeader } from "../../../../Common/Table/TableHeader"
import { OrgDashboardBase } from "../DashboardBase/OrgDashboardBase"
import { workerColumns, workerSortOptions } from "./Common/constants"
import { WorkerAddModal } from "./Common/WorkerAddModal"
import { WorkerAddToGroupModal } from "./Common/WorkerAddToGroupModal"
import { WorkerSubMenu } from "./Common/WorkerSubMenu"

type Modal =
    | { type: "add"; workerType: APIWorkerType }
    | { type: "remove"; worker: APIWorker }
    | { type: "add-to-group" }

const actions: "Add to group"[] = ["Add to group"]

const addWorkerItems: { value: APIWorkerType; label: string }[] = [
    { value: "USER", label: "Human worker" }
    // TODO: comment out below until they become ready to use
    // { value: "AI", label: "AI" },
    // { value: "BOT", label: "Bot" },
    // { value: "CROWD", label: "super.AI's in-house labelers" }
]

export const WorkerList: FC<{
    orgUsername: string
    showAddModal: boolean
    sort: SortExpression<APIWorker>
}> = ({ orgUsername, sort, showAddModal }) => {
    const myEmail = useAppState(getEmail)
    const dispatch = useDispatch<MainThunkDispatch>()
    const [workersFetch, loadMore] = useWorkerList(orgUsername, sort)
    const { modal, open, close, isSaving, save } = useModal<Modal>(
        showAddModal ? { type: "add", workerType: "USER" } : undefined
    )
    const sortOptions = useMemo<SortChooserProps<APIWorker>>(
        () => ({
            fields: workerSortOptions,
            applied: sort,
            onChange: newSort =>
                dispatch(push(routePaths.orgWorkerList(orgUsername, newSort)))
        }),
        [dispatch, orgUsername, sort]
    )
    const columns = useMemo(
        () =>
            workerColumns(
                ["Remove from organization"],
                myEmail ?? "",
                (selected: "Remove from organization", id: number) => {
                    const worker = getValue(workersFetch)?.items.find(
                        worker => worker.id === id
                    )

                    if (worker === undefined) {
                        return
                    }

                    switch (selected) {
                        case "Remove from organization":
                            open({
                                type: "remove",
                                worker
                            })
                            return
                    }
                }
            ),
        [myEmail, open, workersFetch]
    )
    const handleRemoveWorker = useCallback(() => {
        if (modal?.type === "remove") {
            save(removeWorker(orgUsername, modal.worker.id))
        }
    }, [modal, orgUsername, save])
    const handleAdd = useCallback(
        (workerType: APIWorkerType) => open({ type: "add", workerType }),
        [open]
    )
    const handleAddHumanWorker = useCallback(
        (emails: string[]) => {
            if (modal?.type === "add") {
                save(createHumanWorker(orgUsername, emails))
            }
        },
        [modal, orgUsername, save]
    )
    const handleAddBotOrCrowdWorker = useCallback(() => {
        if (
            modal?.type === "add" &&
            (modal.workerType === "BOT" || modal.workerType === "CROWD")
        ) {
            save(createBotOrCrowdWorker(orgUsername, modal.workerType))
        }
    }, [modal, orgUsername, save])
    const count = getValue(workersFetch)?.total
    const [selected, toggleSelection] = useSelection(
        getWorkerId,
        getValue(workersFetch)?.items ?? []
    )
    const selectedIds = useMemo(() => selected.map(getWorkerId), [selected])
    const handleChooseExtraAction = useCallback(
        (selection: "Add to group") => {
            switch (selection) {
                case "Add to group":
                    if (selectedIds.length === 0) {
                        return
                    }

                    return open({ type: "add-to-group" })
            }
        },
        [open, selectedIds.length]
    )
    const handleAddToGroup = useCallback(
        (group: APIWorkerGroup) => {
            if (modal?.type === "add-to-group") {
                save(updateWorkerGroup(orgUsername, group))
            }
        },
        [modal, orgUsername, save]
    )

    useEffect(() => {
        if (modal === undefined) {
            dispatch(replace(routePaths.orgWorkerList(orgUsername)))
        }
    }, [dispatch, modal, orgUsername])

    return (
        <OrgDashboardBase
            fixedContentWidth={false}
            orgUsername={orgUsername}
            title="Workers"
            titleComponent="h1"
            description="Humans, AI, and Bots who work on tasks for you."
        >
            <div className="flex-grow h-0 flex-basis-0 flex items-start overflow-hidden -mb-6 -mr-6">
                <WorkerSubMenu
                    activeMenu="worker"
                    className="mr-6 mt-3"
                    orgUsername={orgUsername}
                />
                <div className="flex-grow h-full flex flex-col">
                    <TableHeader
                        className="mr-6"
                        filteredCount={count}
                        totalCount={count}
                        sortOptions={sortOptions}
                        extraActions={
                            <>
                                <Menu
                                    className="mx-2"
                                    title="Other actions"
                                    disabled={selectedIds.length === 0}
                                    items={actions}
                                    onChoose={handleChooseExtraAction}
                                />
                                <Menu
                                    color="violet"
                                    title="Add worker"
                                    items={addWorkerItems}
                                    align="right"
                                    onChoose={handleAdd}
                                />
                            </>
                        }
                    />
                    <div className="flex-grow h-0 flex-basis-0 overflow-y-auto pr-6">
                        <Table
                            className="w-full"
                            columns={columns}
                            getKey={getWorkerId}
                            items={getValue(workersFetch)?.items}
                            itemCount={count}
                            stickyHeader={true}
                            loadMore={loadMore}
                            checkedItemIds={selectedIds}
                            onToggleRowCheck={toggleSelection}
                        />
                    </div>
                </div>
            </div>
            {modal?.type === "add" && modal.workerType === "USER" && (
                <WorkerAddModal
                    isSaving={isSaving}
                    onClose={close}
                    onSubmit={handleAddHumanWorker}
                />
            )}
            {modal?.type === "add" &&
                (modal.workerType === "BOT" ||
                    modal.workerType === "CROWD") && (
                    <Confirm
                        title={
                            modal.workerType === "BOT"
                                ? "Add a bot worker?"
                                : "Add super.AI workers?"
                        }
                        yesLabel={isSaving ? "Adding..." : "Add"}
                        noLabel="Cancel"
                        isProcessing={isSaving}
                        onClose={close}
                        onClickYes={handleAddBotOrCrowdWorker}
                        onClickNo={close}
                    />
                )}
            {modal?.type === "remove" && (
                <Confirm
                    title={`Remove ${getWorkerDisplayText(modal.worker)}?`}
                    yesLabel={isSaving ? "Removing..." : "Remove"}
                    noLabel="Cancel"
                    isProcessing={isSaving}
                    onClose={close}
                    onClickYes={handleRemoveWorker}
                    onClickNo={close}
                />
            )}
            {modal?.type === "add-to-group" && (
                <WorkerAddToGroupModal
                    orgUsername={orgUsername}
                    isSaving={isSaving}
                    workers={selected}
                    onSubmit={handleAddToGroup}
                    onClose={close}
                />
            )}
        </OrgDashboardBase>
    )
}
